import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Download,
  FileText,
  Folder,
  Monitor,
  ShieldCheck,
  UserRound,
  CircleAlert,
} from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { NGROK } from "../../../../APIs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from "../../../../vendor/Whiteswan-Security-logo.png";
import SelectionFeedback from "./SelectionFeedback";

const resourceDetails = {
  Application: {
    title: "Applications",
    field: "applications",
    icon: FileText,
    name: (item) => item.appName,
  },
  Folder: {
    title: "Folders",
    field: "folders",
    icon: Folder,
    name: (item) => item.path,
  },
  Privilege: {
    title: "Privileges",
    field: "groups",
    icon: ShieldCheck,
    name: (item) => item.adComputerUserGroup.cn,
  },
};

const TablePdf = ({
  selectedProfile,
  selectedTenant,
  activeUsersList,
  activeDevicesList,
  activeResourceType,
}) => {
  const [loading, setLoading] = useState(true);
  const [reportError, setReportError] = useState(false);
  const [deviceWithReports, setDeviceWithReports] = useState([]);
  const handleGeneratePDF = async (resource) => {
    const pdf = new jsPDF();
    const title = "Reports";
    const titleWidth = pdf.getTextWidth(title);
    const pageWidth = pdf.getPageWidth();
    const titleX = (pageWidth - titleWidth) / 2;

    const imgX = (pageWidth - 100) / 2;

    pdf.setFillColor(35, 48, 68);
    pdf.rect(0, 0, pdf.internal.pageSize.width, 40, "F");

    pdf.addImage(Logo, "png", imgX, 5, 100, 30);

    let startY = 50;
    let contentAdded = false;
    let computerUser = "";
    deviceWithReports.forEach((device) => {
      device.users.forEach((user, index) => {
        computerUser = user.cn;
        const userTableData =
          resource === "Application" && user.hasOwnProperty("applications")
            ? [
                ...user.applications.map((app, index) => [
                  index + 1,
                  app.appName,
                  device.dNSHostName,
                  user.cn,
                ]),
              ]
            : resource === "Folder" && user.hasOwnProperty("folders")
              ? [
                  ...user.folders?.map((folder, index) => [
                    index + 1,
                    folder.path,
                    device.dNSHostName,
                    user.cn,
                  ]),
                ]
              : resource === "Privilege" && user.hasOwnProperty("groups")
                ? [
                    ...user.groups.map((group, index) => [
                      index + 1,
                      group.adComputerUserGroup.cn,
                      device.dNSHostName,
                      user.cn,
                    ]),
                  ]
                : [];

        if (userTableData.length > 0) {
          startY = index === 0 ? 50 : 10;

          pdf.text("Reports", titleX, startY);
          pdf.text(
            `Selected tenant: ${selectedTenant.tenantName}`,
            10,
            startY + 10,
          );
          pdf.text(`Resource type: ${resource}`, 10, startY + 20);
          pdf.text(
            `Over-provisioned current profile: ${device.profile}`,
            10,
            startY + 30,
          );
          pdf.text(`New profile: ${selectedProfile.name}`, 10, startY + 40);
          pdf.text(`Computer User: ${computerUser}`, 10, startY + 50);
          pdf.text(`Over-provisioned resources`, 70, startY + 60);

          autoTable(pdf, {
            startY: startY + 70,
            head: [["ID", "Resource", "Device", "User"]],
            body: userTableData,
            headStyles: { fillColor: [35, 48, 68] },
          });

          contentAdded = true;
          if (index < device.users.length - 1) {
            pdf.addPage();
          }
        }
      });
    });

    if (!contentAdded) {
      pdf.text("Reports", titleX, startY);
      pdf.text(
        `Selected tenant: ${selectedTenant.tenantName}`,
        10,
        startY + 10,
      );
      pdf.text(`Resource type: ${resource}`, 10, startY + 20);

      autoTable(pdf, {
        startY: startY + 50,
        head: [["ID", "Resource", "Device", "User"]],
        body: [["", "No data available", "", ""]],
        headStyles: { fillColor: [35, 48, 68] },
        bodyStyles: { halign: "center", valign: "middle" },
      });
    }

    pdf.save("Reports.pdf");
  };

  useEffect(() => {
    let mounted = true;
    let failed = false;
    setLoading(true);
    setReportError(false);
    setDeviceWithReports([]);
    const getAllApplications = async (userId, deviceId) => {
      try {
        const response = await axios.get(
          `${NGROK}/api/computer-user/get-all-apps?userId=${userId}&computerId=${deviceId}`,
        );

        const apps = response.data.content
          .filter((item) => item && item?.computerUsersPublishersAppsMembership)
          .map((item) => item?.computerUsersPublishersAppsMembership);

        return apps;
      } catch (error) {
        failed = true;
        console.error(error);
        return [];
      }
    };

    const getFolders = async (deviceId, userId) => {
      try {
        const response = await axios.get(
          `${NGROK}/api/${selectedTenant.tenantName}/computers/${deviceId}/computer-users/${userId}/folders`,
        );
        return response.data.content;
      } catch (error) {
        failed = true;
        console.error(error, "error");
        return [];
      }
    };

    const getUserGroups = async (userId) => {
      try {
        const response = await axios.get(
          `${NGROK}/api/${selectedTenant.tenantName}/computer-user-groups/membership/${userId}`,
        );
        return response.data.content;
      } catch (error) {
        failed = true;
        console.error(error, "error");
        return [];
      }
    };

    const getAllApplicationsWithPagination = async (profileId) => {
      let allApplications = [];
      try {
        const countResponse = await axios.get(
          `${NGROK}/api/profiles/get-all-apps/count?profileId=${profileId}&profileResource=BLOCKED`,
        );
        const totalCount = countResponse.data;

        const limit = 1000;
        const totalPages = Math.ceil(totalCount / limit);

        let pageCount = 0;

        while (pageCount <= totalPages) {
          try {
            const response = await axios.get(
              `${NGROK}/api/profiles/get-all-apps?profileId=${profileId}&page=${pageCount}&limit=${limit}&profileResource=BLOCKED`,
            );

            const profileApps = response.data
              .filter((item) => item && item?.defaultProfileAppsResource)
              .map((item) => item?.defaultProfileAppsResource);

            if (Array.isArray(profileApps)) {
              allApplications = [...allApplications, ...profileApps];
            } else if (profileApps) {
              allApplications.push(profileApps);
            }
          } catch (e) {
            failed = true;
            console.error(e);
          }

          pageCount++;
        }
      } catch (error) {
        failed = true;
        console.error("Error fetching applications with pagination:", error);
      }
      return allApplications;
    };

    const getProfileFolders = async (profile) => {
      try {
        const response = await axios.get(
          `${NGROK}/api/${profile.os}/profiles/${profile.id}/folders`,
        );
        return response.data.content;
      } catch (error) {
        failed = true;
        console.error(error);
      }
    };

    const getProfileGroups = async (profile) => {
      const response = await axios.get(
        `${NGROK}/api/${profile.os}/profiles/${profile.id}/membership`,
      );
      return response.data.content;
    };

    const fetchData = async () => {
      const appDevices = [];
      const folderDevices = [];
      const groupDevices = [];
      for (const type of activeResourceType) {
        switch (type) {
          case "Application":
            const checkDifferencesInApps = async () => {
              const [devicesWithYesResources, noProfileResource] =
                await Promise.all([
                  await Promise.all(
                    activeDevicesList.map(async (device) => {
                      const usersWithApps = await Promise.all(
                        activeUsersList.map(async (user) => {
                          const allApplications = await getAllApplications(
                            user.id,
                            device.id,
                          );

                          const applications = allApplications?.filter(
                            (app) =>
                              app.groupStatus === "ENABLED" ||
                              app.inheritedGroupStatus === "ENABLED",
                          );
                          return { ...user, applications };
                        }),
                      );
                      return { ...device, users: usersWithApps };
                    }),
                  ),
                  await getAllApplicationsWithPagination(
                    selectedProfile.id,
                    "BLOCKED",
                  ),
                ]);

              const matchedDevices = devicesWithYesResources
                .map((device) => {
                  const matchedUsers = device.users
                    .map((user) => {
                      const matchedApplications = user.applications?.filter(
                        (app) =>
                          noProfileResource.some(
                            (noApp) => noApp.appId === app.appId,
                          ),
                      );
                      return { ...user, applications: matchedApplications };
                    })
                    .filter((user) => user.applications?.length > 0);

                  return {
                    ...device,
                    users: matchedUsers,
                  };
                })
                .filter((device) => device.users?.length > 0);

              return matchedDevices;
            };

            try {
              const devices = await checkDifferencesInApps();
              appDevices.push(...devices);
            } catch (error) {
              failed = true;
              console.error(error, "Error during app comparison");
            }
            break;
          case "Folder":
            const checkDifferencesInFolders = async () => {
              const devicesWithYesFolders = await Promise.all(
                activeDevicesList.map(async (device) => {
                  const usersWithFolders = await Promise.all(
                    activeUsersList.map(async (user) => {
                      const allFolders = await getFolders(device.id, user.id);
                      const folders = allFolders?.filter(
                        (folder) =>
                          folder.groupStatus === "ENABLED" ||
                          folder.inheritedGroupStatus === "ENABLED",
                      );
                      return { ...user, folders };
                    }),
                  );
                  return { ...device, users: usersWithFolders };
                }),
              );

              const profileFolders = await getProfileFolders(selectedProfile);

              const noProfileResource = profileFolders.filter(
                (folder) => folder.profileResource === "BLOCKED",
              );

              const matchedDevices = devicesWithYesFolders
                .map((device) => {
                  const matchedUsers = device.users
                    .map((user) => {
                      const matchedFolders = user.folders?.filter((yesFolder) =>
                        noProfileResource.some(
                          (noFolder) =>
                            noFolder.folderId === yesFolder.folderId,
                        ),
                      );
                      return { ...user, folders: matchedFolders };
                    })
                    .filter((user) => user.folders.length > 0);
                  return {
                    ...device,
                    users: matchedUsers,
                  };
                })
                .filter((device) => device.users.length > 0);

              return matchedDevices;
            };
            try {
              const devices = await checkDifferencesInFolders();
              folderDevices.push(...devices);
            } catch (error) {
              failed = true;
              console.error(error, "error");
            }

            break;
          case "Privilege":
            const checkDifferencesInGroups = async () => {
              const devicesWithYesGroups = await Promise.all(
                activeDevicesList.map(async (device) => {
                  const usersWithGroups = await Promise.all(
                    activeUsersList.map(async (user) => {
                      const allGroups = await getUserGroups(user.id);
                      const groups = allGroups?.filter(
                        (group) =>
                          group.groupStatus === "ENABLED" ||
                          group.inheritedGroupStatus === "ENABLED",
                      );
                      return { ...user, groups };
                    }),
                  );
                  return { ...device, users: usersWithGroups };
                }),
              );

              const profileGroups = await getProfileGroups(selectedProfile);

              const noProfileResource = profileGroups.filter(
                (group) => group.profileResource === "BLOCKED",
              );

              const matchedDevices = devicesWithYesGroups
                .map((device) => {
                  const matchedUsers = device.users
                    .map((user) => {
                      const matchedGroups = user.groups?.filter((yesGroup) =>
                        noProfileResource.some(
                          (noGroup) =>
                            noGroup.groupId === yesGroup.adComputerUserGroup.id,
                        ),
                      );
                      return { ...user, groups: matchedGroups };
                    })
                    .filter((user) => user.groups.length > 0);
                  return {
                    ...device,
                    users: matchedUsers,
                  };
                })
                .filter((device) => device.users.length > 0);

              return matchedDevices;
            };
            try {
              const devices = await checkDifferencesInGroups();
              groupDevices.push(...devices);
            } catch (error) {
              failed = true;
              console.error(error, "error");
            }
            break;
          default:
        }
      }
      return { appDevices, folderDevices, groupDevices };
    };

    const unifyDevicesArrays = (devices) => {
      const allDevicesMap = {};

      devices.forEach((device) => {
        if (!allDevicesMap[device.id]) {
          allDevicesMap[device.id] = {
            ...device,
            users: {
              ...device.users.reduce(
                (acc, user) => ({ ...acc, [user.id]: user }),
                {},
              ),
            },
          };
        } else {
          const existingDevice = allDevicesMap[device.id];
          device.users.forEach((user) => {
            if (existingDevice.users[user.id]) {
              existingDevice.users[user.id] = {
                ...existingDevice.users[user.id],
                ...user,
              };
            } else {
              existingDevice.users[user.id] = user;
            }
          });
        }
      });

      const combinedDevices = Object.values(allDevicesMap).map((device) => ({
        ...device,
        users: Object.values(device.users).sort((a, b) => a.id - b.id),
      }));

      combinedDevices.sort((a, b) => a.id - b.id);

      return combinedDevices;
    };

    const generateReport = async () => {
      try {
        const { appDevices, folderDevices, groupDevices } = await fetchData();
        const devices = unifyDevicesArrays([
          ...appDevices,
          ...folderDevices,
          ...groupDevices,
        ]);
        if (mounted) setDeviceWithReports(devices);
      } catch (error) {
        console.error(error);
        failed = true;
      } finally {
        if (mounted) {
          setReportError(failed);
          setLoading(false);
        }
      }
    };
    generateReport();
    return () => {
      mounted = false;
    };
  }, [
    activeResourceType,
    activeDevicesList,
    activeUsersList,
    selectedProfile,
    selectedTenant,
  ]);

  return (
    <div
      className="op-results"
      aria-label="Comparison results"
      aria-busy={loading}
    >
      {reportError && (
        <div className="op-report-error" role="alert">
          <CircleAlert aria-hidden="true" />
          <div>
            <h3>Comparison could not be completed</h3>
            <p>
              Some access data could not be loaded. Results may be incomplete.
              Go back and generate the report again to retry.
            </p>
          </div>
        </div>
      )}
      {activeResourceType.map((resource) => {
        const details = resourceDetails[resource];
        const Icon = details.icon;
        const rows = deviceWithReports.flatMap((device) =>
          device.users.flatMap((user) =>
            (user[details.field] || []).map((item) => ({
              name: details.name(item),
              device: device.dNSHostName,
              user: user.cn,
            })),
          ),
        );
        return (
          <section className="op-card op-result-card" key={resource}>
            <div className="op-result-header">
              <div className="op-heading">
                <span className="op-icon">
                  <Icon aria-hidden="true" />
                </span>
                <div>
                  <h2>{details.title}</h2>
                  <p>
                    {loading
                      ? "Comparing enabled access with the selected profile…"
                      : `${rows.length} ${rows.length === 1 ? "match" : "matches"} against ${selectedProfile.name}`}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                disabled={loading}
                onClick={() => handleGeneratePDF(resource)}
              >
                <Download aria-hidden="true" />
                Export PDF
              </Button>
            </div>
            {loading ? (
              <SelectionFeedback loading />
            ) : rows.length ? (
              <div className="op-result-table">
                <table aria-label={`${details.title} comparison results`}>
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">{resource}</th>
                      <th scope="col">Device</th>
                      <th scope="col">User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={`${row.device}-${row.user}-${index}`}>
                        <td>{index + 1}</td>
                        <td>
                          <span className="op-resource-name">
                            <Icon aria-hidden="true" />
                            {row.name}
                          </span>
                        </td>
                        <td>
                          <span className="op-table-identity">
                            <Monitor aria-hidden="true" />
                            {row.device}
                          </span>
                        </td>
                        <td>
                          <span className="op-table-identity">
                            <UserRound aria-hidden="true" />
                            {row.user}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <SelectionFeedback
                error={reportError}
                title={
                  reportError
                    ? "Comparison data unavailable"
                    : "No matching access found"
                }
                description={
                  reportError
                    ? "Regenerate the report once the access data is available."
                    : `No enabled ${details.title.toLowerCase()} in this scope matched resources blocked by ${selectedProfile.name}.`
                }
              />
            )}
          </section>
        );
      })}
    </div>
  );
};

export default TablePdf;
